import { ReactRenderer, posToDOMRect, Editor } from '@tiptap/react';
import { computePosition, flip, shift } from '@floating-ui/dom';
import type { MentionNodeAttrs } from '@tiptap/extension-mention';
import type { SuggestionOptions } from '@tiptap/suggestion';
import OptionList from '../../components/tiptap/OptionList';

const updatePosition = (editor: Editor, element: HTMLElement) => {
  const virtualElement = {
    getBoundingClientRect: () =>
      posToDOMRect(editor.view, editor.state.selection.from, editor.state.selection.to),
  };

  computePosition(virtualElement, element, {
    placement: 'bottom-start',
    strategy: 'absolute',
    middleware: [shift(), flip()],
  }).then(({ x, y, strategy }) => {
    element.style.width = 'max-content';
    element.style.position = strategy;
    element.style.left = `${x}px`;
    element.style.top = `${y}px`;
  });
};

/* eslint-disable @typescript-eslint/no-explicit-any */
const suggestionConfig = (
  options: string[],
): Omit<SuggestionOptions<any, MentionNodeAttrs>, 'editor'> => ({
  items({ query }) {
    return options
      .filter((m) => m.toLowerCase().trim().startsWith(query.toLowerCase().trim()))
      .slice(0, 5);
  },
  render() {
    let component: ReactRenderer;

    return {
      onStart(props) {
        component = new ReactRenderer(OptionList, {
          props,
          editor: props.editor,
        });

        if (!props.clientRect) {
          return;
        }

        component.element.style.position = 'absolute';
        component.element.style.zIndex = '50';

        document.body.appendChild(component.element);

        updatePosition(props.editor, component.element);
      },

      onUpdate(props) {
        component.updateProps(props);

        if (!props.clientRect) {
          return;
        }

        updatePosition(props.editor, component.element);
      },

      onKeyDown(props) {
        if (props.event.key === 'Escape') {
          component.destroy();

          return true;
        }

        return (component.ref as any)?.onKeyDown(props);
      },

      onExit() {
        component.element.remove();
        component.destroy();
      },
    };
  },
});

export default suggestionConfig;
